import { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSectionProps {
  faqs: FAQItem[];
  title?: string;
}

export default function FAQSection({ faqs, title = 'Frequently Asked Questions' }: FAQSectionProps) {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section className="bg-white py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">

          {/* Left */}
          <div className="lg:col-span-4">
            <p className="text-ecos-green text-xs uppercase tracking-widest font-semibold mb-4">FAQ</p>
            <h2 className="font-serif text-ecos-charcoal text-4xl lg:text-5xl font-semibold leading-tight mb-6">
              {title}
            </h2>
            <div className="w-10 h-0.5 bg-ecos-green mb-8"></div>
            <p className="text-ecos-text text-sm leading-relaxed mb-8">
              Can&apos;t find what you&apos;re looking for? Our certified environmental specialists are available to answer your questions directly.
            </p>
            <a
              href="tel:+18888685420"
              className="inline-flex items-center gap-2 bg-ecos-green hover:bg-ecos-green-light text-white px-5 py-3 rounded-sm text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-line text-sm"></i>
              </div>
              Speak With a Specialist
            </a>
          </div>

          {/* Right — accordion */}
          <div className="lg:col-span-8">
            <div className="divide-y divide-ecos-border">
              {faqs.map((faq, idx) => (
                <div key={faq.question}>
                  <button
                    className="w-full flex items-center justify-between text-left py-6 gap-6 cursor-pointer group"
                    onClick={() => setOpen(open === idx ? null : idx)}
                  >
                    <h3 className="font-serif text-ecos-charcoal text-lg font-medium group-hover:text-ecos-green transition-colors leading-snug">
                      {faq.question}
                    </h3>
                    <div
                      className={`w-8 h-8 flex items-center justify-center rounded-sm transition-all duration-300 flex-shrink-0 ${
                        open === idx
                          ? 'bg-ecos-green text-white'
                          : 'bg-ecos-slate text-ecos-text group-hover:bg-ecos-green-pale'
                      }`}
                    >
                      <i className={`text-base transition-transform duration-300 ${open === idx ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                    </div>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      open === idx ? 'max-h-96 opacity-100 pb-6' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <p className="text-ecos-text text-base leading-relaxed max-w-2xl">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
